import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type { FeatureCollection } from 'geojson';

import { NETWORK_SCOPE, resolveStatsScope, type StatsScope } from '@/components/map/utils/resolveStatsScope';
import type { SemanticZoomLevel } from '@/components/map/utils/semanticZoom';

export interface StatsScopeResolverOptions {
    mapRef: RefObject<MapRef | null>;
    isMapLoaded: boolean;
    /** False disables the resolver entirely (SAVI mode — no context-aware stats bar). */
    enabled: boolean;
    /**
     * Re-resolve trigger only — the scope maths reads the live `map.getZoom()`
     * (the Network Overview band splits into network/area-focus zones mid-band,
     * which the semantic level can't express). Stepper-driven level changes
     * re-resolve immediately through this prop; everything else via moveend.
     */
    semanticZoomLevel: SemanticZoomLevel;
    clientId: string | null;
    getAreasData: () => FeatureCollection;
    areasDataVersion: number;
    getDmasData: () => FeatureCollection;
    dmasDataVersion: number;
    /**
     * A clicked AOI/DMA scope that overrides the map-centre resolution: while set, the
     * Global Stats bar shows the clicked feature regardless of the centre or zoom. Null
     * falls back to resolving from the map centre. Must be referentially stable across
     * pan renders (memoise it) so it doesn't retrigger resolution needlessly.
     */
    selectionScope: StatsScope | null;
    setScope: (scope: StatsScope) => void;
}

// Matches the viewport-loader and URL-sync debounces so one settled pan coalesces
// into a single scope resolution.
const RESOLVE_DEBOUNCE_MS = 300;

export interface StatsScopeResolverApi {
    /**
     * Publishes the network scope and holds it until the next settled map move —
     * the reset-to-network action (stats bar Reset, Locate Network map control) clears
     * the selection and flies out at the same time, and without the hold the selection
     * clearing would re-resolve from the map centre mid-flight (still zoomed in over the
     * old feature) and flick the scope straight back. The flight's own `moveend` lands at
     * the Network Overview zoom, which resolves to the network scope anyway.
     *
     * Only call this when a flight is actually starting — a `moveend` is what lifts the hold.
     */
    resetToNetworkScope: () => void;
}

/**
 * Resolves which Area of Interest / DMA contains the map centre and publishes it
 * as the Global Stats bar's scope. Runs debounced on `moveend`, and immediately
 * when the semantic zoom level, the loaded polygon data, or the client changes —
 * the data-version triggers cover the pan race where a moveend resolution runs
 * before the DMA viewport fetch has landed.
 *
 * A clicked AOI/DMA (`selectionScope`) overrides all of this: while a feature is
 * selected the bar shows that feature and centre resolution is suppressed; clearing
 * the selection re-resolves from the centre.
 */
export function useStatsScopeResolver(options: StatsScopeResolverOptions): StatsScopeResolverApi {
    const {
        mapRef,
        isMapLoaded,
        enabled,
        semanticZoomLevel,
        clientId,
        getAreasData,
        areasDataVersion,
        getDmasData,
        dmasDataVersion,
        selectionScope,
        setScope,
    } = options;

    // Set by resetToNetworkScope, cleared by the next moveend: holds the network scope
    // across the fly-out so the mid-flight re-resolutions can't re-pin the old feature.
    const holdNetworkScopeRef = useRef(false);

    // The resolver reads its inputs through a ref so the moveend listener stays
    // attached across semantic-level/data changes without re-subscribing.
    const resolveRef = useRef<() => void>(() => {});
    useLayoutEffect(() => {
        resolveRef.current = () => {
            if (!enabled) return;
            if (holdNetworkScopeRef.current) return;
            // A clicked AOI/DMA pins the scope — ignore the map centre while one is selected.
            if (selectionScope) {
                setScope(selectionScope);
                return;
            }
            const map = mapRef.current?.getMap();
            if (!map) return;
            const centre = map.getCenter();
            setScope(resolveStatsScope([centre.lng, centre.lat], map.getZoom(), getAreasData(), getDmasData()));
        };
    });

    // Debounced re-resolution on every settled map move.
    useEffect(() => {
        if (!enabled || !isMapLoaded) return;
        const map = mapRef.current?.getMap();
        if (!map) return;

        let debounceTimer: ReturnType<typeof setTimeout> | undefined;
        const handleMoveEnd = () => {
            // The move that lifts a reset's hold — from here the centre is authoritative again.
            holdNetworkScopeRef.current = false;
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                resolveRef.current();
            }, RESOLVE_DEBOUNCE_MS);
        };

        map.on('moveend', handleMoveEnd);
        return () => {
            clearTimeout(debounceTimer);
            map.off('moveend', handleMoveEnd);
        };
    }, [enabled, isMapLoaded, clientId, mapRef]);

    // Reset to network scope whenever the client changes; the scoped caches are
    // client-keyed, but the previous client's area/DMA must never linger while
    // the new client's polygons load.
    useEffect(() => {
        if (!enabled) return;
        holdNetworkScopeRef.current = false;
        setScope(NETWORK_SCOPE);
    }, [enabled, clientId, setScope]);

    // Immediate re-resolution on discrete events: semantic level changes (stepper
    // or moveend), polygon data arriving/clearing, client switches, and the clicked
    // selection changing (setting one pins its scope; clearing one falls back to the
    // centre). Move storms are already smoothed by the moveend debounce above.
    useEffect(() => {
        if (!enabled || !isMapLoaded) return;
        resolveRef.current();
    }, [enabled, isMapLoaded, semanticZoomLevel, areasDataVersion, dmasDataVersion, clientId, selectionScope]);

    const resetToNetworkScope = useCallback(() => {
        if (!enabled) return;
        holdNetworkScopeRef.current = true;
        setScope(NETWORK_SCOPE);
    }, [enabled, setScope]);

    return { resetToNetworkScope };
}
